import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { Badge, Field, FieldGrid, SearchCombobox, TextArea, TextInput } from '@zarya/ui';
import { t } from '@app/i18n';
import { OwnerCombobox } from '@app/shared/OwnerCombobox';
import type { ApplicationUser } from '@app/shared/owner';
import type { Resource } from './Resources.api';
import { textOrigin, type ResourceFormValues } from './ResourceModel';
import { kind as kindBadge } from './resourceVocab';
import s from './Resources.module.css';

/**
 * Aba **Geral** do drawer T7 (FR-029).
 *
 * O identificador é a chave do catálogo no middleware: somente leitura para o
 * que veio do gateway, e imutável depois de criado para os manuais. O tipo muda
 * as colunas da aba Tools, então também não muda depois de criado.
 *
 * Sem autenticação downstream e sem referência a cofre (FR-038): quem se
 * autentica no downstream é o gateway, não este console.
 */

/**
 * As ajudas ⓘ e as opções de tipo saem do catálogo no render
 * (`resources.form.*`): constante de módulo congela o idioma da importação.
 */
const kindOptions = () => [
  { value: 'mcp', label: t('resources.filters.kind.mcp') },
  { value: 'api', label: t('resources.filters.kind.api') },
];

/** Cadastro e ambientes usados no catálogo mais o valor corrente (inclusive texto livre). */
function ambienteOptions(ambientes: string[], current: string) {
  const lista = current && !ambientes.includes(current) ? [current, ...ambientes] : ambientes;
  return lista.map((nome) => ({ value: nome, label: nome }));
}

export interface ResourceFormProps {
  /** Ausente = criação (só resources manuais nascem pela interface, R22). */
  resource?: Resource;
  control: Control<ResourceFormValues>;
  errors: FieldErrors<ResourceFormValues>;
  /** Usuários ativos da aplicação (`listUsers`) — as opções do Dono. */
  usuarios: ApplicationUser[];
  /** Ambientes já usados por outros resources (`listResources`) — sugestões do Ambiente. */
  ambientes: string[];
  /** `false` = leitura (approver/viewer, FR-010). */
  editavel: boolean;
  tipo: ResourceFormValues['tipo'];
  /**
   * Sites do tenant (slug + id). **Lista vazia esconde o campo** — revelação
   * progressiva (P1): com um cluster, um seletor de uma opção promete uma escolha
   * que não existe. Quem decide é `useSiteSlugs`, que devolve vazio sem `multi`.
   */
  sites: { id: string; slug: string }[];
}

export function ResourceForm({
  resource,
  control,
  errors,
  usuarios,
  ambientes,
  editavel,
  tipo,
  sites,
}: ResourceFormProps) {
  const criando = !resource;
  const importado = resource?.origin === 'apim';
  // Identificador e tipo são imutáveis depois de criados; para importados o
  // identificador é o id do gateway e nunca é editável.
  const identifierTravado = !criando || importado || !editavel;
  const kindTravado = !criando || !editavel;

  return (
    <FieldGrid>
      <Field
        label={t('resources.form.identifier.label')}
        span={6}
        required
        error={errors.apim_id?.message ?? null}
        // Os dois ramos diziam a mesma coisa; virou uma chave só.
        help={t('resources.form.identifier.help')}
        tip={t('resources.form.tip.identifier')}
      >
        {(props) => (
          <Controller
            control={control}
            name="apim_id"
            render={({ field }) => (
              <TextInput
                {...props}
                {...field}
                mono
                readOnly={identifierTravado}
                placeholder={t('resources.form.identifier.placeholder')}
              />
            )}
          />
        )}
      </Field>

      {sites.length > 0 ? (
        <Field
          label={t('resources.form.site.label')}
          span={6}
          help={t(resource ? 'resources.form.site.help.edit' : 'resources.form.site.help.new')}
          tip={t('resources.form.site.tip')}
          error={errors.site_id?.message ?? null}
        >
          {({ className: _unused, ...props }) => (
            <Controller
              control={control}
              name="site_id"
              render={({ field }) => (
                <SearchCombobox
                  {...props}
                  label={t('resources.form.site.label')}
                  // SOMENTE LEITURA NA EDIÇÃO: recurso não muda de site, pela mesma
                  // razão do slug — o site é a âncora do endereçamento, e movê-lo
                  // trocaria o gateway que descreve o recurso sem trocar o recurso.
                  disabled={!editavel || Boolean(resource)}
                  options={sites.map((site) => ({
                    value: site.id,
                    label: site.slug,
                    mono: true,
                  }))}
                  value={field.value || null}
                  onChange={(valor) => field.onChange(valor ?? '')}
                  placeholder={t('resources.form.site.placeholder')}
                />
              )}
            />
          )}
        </Field>
      ) : null}

      <Field
        label={t('resources.form.name.label')}
        span={6}
        help={t('resources.form.name.help')}
        error={errors.nome?.message ?? null}
      >
        {(props) => (
          <Controller
            control={control}
            name="nome"
            render={({ field }) => (
              <TextInput {...props} {...field} readOnly={!editavel} placeholder={t('resources.form.name.placeholder')} />
            )}
          />
        )}
      </Field>

      <Field
        label={t('resources.form.kind.label')}
        span={6}
        required
        help={t('resources.form.kind.help')}
        tip={t('resources.form.tip.kind')}
        error={errors.tipo?.message ?? null}
      >
        {(props) =>
          kindTravado ? (
            <span className={s.leituraValor} id={props.id}>
              <Badge tone={kindBadge(tipo).tone}>{kindBadge(tipo).rotulo}</Badge>
            </span>
          ) : (
            <Controller
              control={control}
              name="tipo"
              render={({ field }) => (
                <SearchCombobox
                  id={props.id}
                  aria-describedby={props['aria-describedby']}
                  label={t('resources.form.kind.label')}
                  options={kindOptions()}
                  value={field.value}
                  onChange={(valor) => field.onChange(valor ?? 'mcp')}
                  placeholder={t('resources.form.kind.placeholder')}
                />
              )}
            />
          )
        }
      </Field>

      <Field
        label={t('resources.form.origin.label')}
        span={6}
        help={t('resources.form.origin.help')}
      >
        {(props) => (
          <span className={s.leituraValor} id={props.id}>
            {textOrigin(resource)}
          </span>
        )}
      </Field>

      {/* Obrigatório **na criação**, como no contrato (`ResourceCreate` o exige;
          `ResourceUpdate.endpoint` é anulável): importado que chegou sem
          endpoint não pode travar o Salvar de quem só classifica tools. */}
      <Field
        label={t('resources.form.endpoint.label')}
        span={12}
        required={criando}
        help={criando ? undefined : t('resources.form.endpoint.help')}
        error={errors.endpoint?.message ?? null}
        tip={t(
          tipo === 'mcp' ? 'resources.form.tip.endpointMcp' : 'resources.form.tip.endpointApi',
        )}
      >
        {(props) => (
          <Controller
            control={control}
            name="endpoint"
            render={({ field }) => (
              <TextInput
                {...props}
                {...field}
                mono
                readOnly={!editavel}
                placeholder={t(
                  tipo === 'mcp'
                    ? 'resources.form.endpoint.placeholderMcp'
                    : 'resources.form.endpoint.placeholderApi',
                )}
              />
            )}
          />
        )}
      </Field>

      <Field
        label={t('resources.form.owner.label')}
        span={6}
        help={t('resources.form.owner.help')}
        tip={t('resources.form.tip.owner')}
        error={errors.owner?.message ?? null}
      >
        {(props) => (
          <Controller
            control={control}
            name="owner"
            render={({ field }) => (
              // O mesmo componente de T5 (28/08): o usuário logado é a primeira
              // opção, com «(você)». Não é `creatable` — dono é uma pessoa
              // cadastrada, não texto livre.
              <OwnerCombobox
                id={props.id}
                aria-describedby={props['aria-describedby']}
                users={usuarios}
                value={field.value}
                onChange={field.onChange}
                disabled={!editavel}
              />
            )}
          />
        )}
      </Field>

      <Field
        label={t('resources.form.environment.label')}
        span={6}
        tip={t('resources.form.tip.environment')}
        help={t('resources.form.environment.help')}
        error={errors.environment?.message ?? null}
      >
        {(props) => (
          <Controller
            control={control}
            name="environment"
            render={({ field }) => (
              <SearchCombobox
                id={props.id}
                aria-describedby={props['aria-describedby']}
                label={t('resources.form.environment.label')}
                options={ambienteOptions(ambientes, field.value)}
                value={field.value || null}
                onChange={(valor) => field.onChange(valor ?? '')}
                placeholder={t('resources.form.environment.placeholder')}
                searchPlaceholder={t('resources.form.environment.searchPlaceholder')}
                creatable
                disabled={!editavel}
                onCreate={(nome) => field.onChange(nome)}
              />
            )}
          />
        )}
      </Field>

      <Field
        label={t('resources.form.description.label')}
        span={12}
        error={errors.descricao?.message ?? null}
      >
        {(props) => (
          <Controller
            control={control}
            name="descricao"
            render={({ field }) => (
              <TextArea
                {...props}
                {...field}
                readOnly={!editavel}
                rows={2}
                placeholder={t('resources.form.description.placeholder')}
              />
            )}
          />
        )}
      </Field>
    </FieldGrid>
  );
}
